/**
 * Localization Form Handler
 * Updates available languages when a country is selected
 */
class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.countrySelect = this.querySelector('#localization-country');
    this.languageSelect = this.querySelector('#localization-language');
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');

    if (!this.countrySelect || !this.languageSelect || !this.form) return;

    this.setupEventListeners();
    this.prepareHiddenForm();
  }

  setupEventListeners() {
    // Listen for country changes
    this.countrySelect.addEventListener('change', this.onCountryChange.bind(this));
  }

  prepareHiddenForm() {
    // Create a hidden iframe to handle the form submission without navigation
    this.iframe = document.createElement('iframe');
    this.iframe.style.display = 'none';
    this.iframe.name = 'localization-hidden-frame';
    document.body.appendChild(this.iframe);

    // Create a clone of the form to use for submissions
    this.hiddenForm = this.form.cloneNode(true);
    this.hiddenForm.style.display = 'none';
    this.hiddenForm.target = 'localization-hidden-frame';
    this.hiddenForm.id = 'localization-hidden-form';

    // Add a hidden input for the method
    const methodInput = document.createElement('input');
    methodInput.type = 'hidden';
    methodInput.name = '_method';
    methodInput.value = 'patch';
    this.hiddenForm.appendChild(methodInput);

    // Add the form to the document
    document.body.appendChild(this.hiddenForm);

    // Listen for iframe load events
    this.iframe.addEventListener('load', this.onIframeLoad.bind(this));
  }

  onCountryChange() {
    const fallbackSelect = this.countrySelect.querySelector('select');

    if (!fallbackSelect) return;

    const selectedCountry = fallbackSelect.value;

    if (!selectedCountry) return;

    // Show loading state
    this.setLoadingState(true);

    // Update the country_code in the hidden form
    const hiddenCountrySelect = this.hiddenForm.querySelector('select[name="country_code"]');
    if (hiddenCountrySelect) {
      hiddenCountrySelect.value = selectedCountry;
    } else {
      // If no select exists, create a hidden input
      const countryInput = document.createElement('input');
      countryInput.type = 'hidden';
      countryInput.name = 'country_code';
      countryInput.value = selectedCountry;
      this.hiddenForm.appendChild(countryInput);
    }

    // Submit the hidden form
    this.hiddenForm.submit();
  }

  onIframeLoad() {
    try {
      // Try to access the iframe content (may fail due to same-origin policy)
      const iframeContent = this.iframe.contentDocument || this.iframe.contentWindow.document;

      // Find the language form group in the iframe
      const iframeLanguageFormGroup = this.findLanguageFormGroup(iframeContent);

      if (iframeLanguageFormGroup) {
        // Find the current language form group
        const currentLanguageFormGroup = this.findLanguageFormGroup(this);

        if (currentLanguageFormGroup) {
          // Replace just the language select content
          this.updateLanguageSelect(currentLanguageFormGroup, iframeLanguageFormGroup);
        }
      }
    } catch {
      // Alternative approach: reload the dialog without navigation
      this.requestDialog();
    }

    this.setLoadingState(false);
  }

  requestDialog() {
    // Dispatch a custom event for the dialog to refresh its content
    const event = new CustomEvent('localization:country-changed', {
      bubbles: true,
      detail: {
        formId: this.form.id,
      },
    });
    this.dispatchEvent(event);
  }

  updateLanguageSelect(currentGroup, newGroup) {
    // Find the language select elements
    const currentSelect =
      currentGroup.querySelector('content-select') ||
      currentGroup.querySelector('select[name="language_code"]');
    const newSelect =
      newGroup.querySelector('content-select') ||
      newGroup.querySelector('select[name="language_code"]');

    if (currentSelect && newSelect) {
      // Replace the select content
      currentSelect.innerHTML = newSelect.innerHTML;

      // Refresh the content select if it's a custom element
      if (currentSelect.tagName.toLowerCase() === 'content-select') {
        setTimeout(() => {
          currentSelect.dispatchEvent(new CustomEvent('content-select:refresh'));
        }, 0);
      }
    }
  }

  findLanguageFormGroup(container) {
    if (!container) return null;

    // Try to find by class
    const formGroups = container.querySelectorAll('.form-group');

    for (const group of formGroups) {
      // Check if this form group contains a language select
      if (
        group.querySelector('#localization-language') ||
        group.querySelector('[name="language_code"]')
      ) {
        return group;
      }
    }

    return null;
  }

  setLoadingState(isLoading) {
    const button = this.languageSelect?.querySelector('button');
    if (!button) return;

    if (isLoading) {
      button.classList.add('loading');
    } else {
      button.classList.remove('loading');
    }
  }
}

customElements.define('localization-form', LocalizationForm);
